<script setup lang="ts">
import { computed, ref, toRef } from 'vue'
import { RouterLink } from 'vue-router'
import { storeToRefs } from 'pinia'
import { NAlert, NButton, NEmpty, NSkeleton, NTree } from 'naive-ui'
import type { WikiPage } from '../stores/wiki_pages'
import { useSessionStore } from '../stores/session'
import { useCatalogTree } from '../composables/useCatalogTree'
import { useIsMobile } from '../composables/useIsMobile'
import { useSidebarSearch } from '../composables/useSidebarSearch'
import { useSidebarWidth } from '../composables/useSidebarWidth'
import WikiSearchPanel from './WikiSearchPanel.vue'
import WikiSidebarSearchResults from './WikiSidebarSearchResults.vue'

const props = defineProps<{
  pages: WikiPage[]
  loaded: boolean
  loadError: string | null
  selectedId: number | null
  hiddenMobile: boolean
}>()

const emit = defineEmits<{
  (e: 'select-page', id: number): void
  (e: 'select-path', path: string): void
  (e: 'retry-load'): void
  // Mobile-only dismiss: re-hide the catalog without picking a page.
  (e: 'close'): void
}>()

// Catalog vs search is branched on `isSearchMode` — when the query
// passes the FTS threshold the grouped tree is swapped for the
// results list (rendered by WikiSidebarSearchResults). The
// input-with-hint UI lives in WikiSearchPanel; we keep the
// composable instance for the panel prop AND destructure individual
// refs for the template (auto-unwrap drops the `.value` clutter).
const search = useSidebarSearch()
const {
  query: searchQuery,
  results: searchResults,
  searching: searchPending,
  error: searchError,
  isSearchMode,
  runSearch,
  clear: clearSearch,
} = search
const searchPanelRef = ref<InstanceType<typeof WikiSearchPanel> | null>(null)
const isMobile = useIsMobile()

// Admin-only pinned nav (the Memory link below). Read the session
// directly rather than prop-drilling through HomeView → RouteShell —
// the sidebar is the natural owner of "what extra catalog destinations
// can this user reach?" The backend admin-gates the memory API,
// so this is purely a discoverability affordance.
const { currentUser } = storeToRefs(useSessionStore())
const isAdmin = computed(() => currentUser.value?.admin === true)
const { width: sidebarWidth, startResize } = useSidebarWidth()

// Catalog tree concerns (option building, expansion semantics, render
// label, selection parsing) live in their own composable so this view
// stays focused on orchestration. Bridge `props` into refs the
// composable can watch.
const {
  treeOptions: catalogTreeOptions,
  selectedKeys: selectedTreeKeys,
  expandedKeys,
  parseSelection,
  renderLabel,
  rowClickBehavior,
} = useCatalogTree(toRef(props, 'pages'), toRef(props, 'selectedId'), isMobile)

// NTree's `pattern` prop does in-memory case-insensitive label
// filtering — instant, no network. Feeding the search input's value
// lets a 1-character query narrow the catalog to title matches
// without waiting for FTS (which only kicks in at 2+ chars). At 2+
// chars the `isSearchMode` branch below swaps the tree for the FTS
// results list, so this pattern is only relevant in the 0-1 char
// window.
const treePattern = computed(() => searchQuery.value.trim())

function onTreeSelect(keys: Array<string | number>): void {
  const id = parseSelection(keys)
  if (id != null) emit('select-page', id)
}

// Forward a search-result selection up to the host AND dismiss the
// mobile soft keyboard so the article isn't covered by the panel.
// The blur stays in the parent because it owns the search-panel ref;
// the child only knows about the result row.
function onSearchSelect(path: string): void {
  emit('select-path', path)
  searchPanelRef.value?.blur()
}
</script>

<template>
  <aside
    data-testid="sidebar"
    class="sidebar-shell relative border-r border-white/10 flex flex-col sm:shrink-0 max-sm:w-full max-sm:flex-1 max-sm:border-r-0 max-sm:border-white/10"
    :class="{ 'max-sm:hidden': hiddenMobile }"
    :style="isMobile ? undefined : { width: `${sidebarWidth}px` }"
  >
    <!-- Mobile-only catalog dismiss: "Browse catalog" then back
         out without selecting a page. Hidden on desktop (the rail is
         always present) and redundant-but-explicit on `!hiddenMobile`. -->
    <div v-if="!hiddenMobile" class="sm:hidden flex justify-end px-3 pt-2 -mb-1">
      <NButton size="tiny" quaternary data-testid="sidebar-close" aria-label="Close catalog" @click="emit('close')"
        >✕</NButton
      >
    </div>

    <!-- Search -->
    <div class="p-3 border-b border-white/10">
      <WikiSearchPanel ref="searchPanelRef" :search="search" size="small" />
    </div>

    <!-- Pinned admin nav. Memory is reachable here from the left
         sidebar (desktop) and the full-screen Browse-catalog drawer
         (mobile), not only the Admin dropdown. Hidden in search mode so
         the FTS results own the panel. -->
    <nav v-if="isAdmin && !isSearchMode" class="px-3 py-2 border-b border-white/10" data-testid="sidebar-pinned-nav">
      <RouterLink
        to="/memory"
        data-testid="sidebar-memory-link"
        class="flex items-center gap-2 px-2 py-1.5 rounded text-sm text-white/70 hover:text-white hover:bg-white/[0.04] no-underline transition-colors"
        active-class="!text-white bg-white/[0.06]"
      >
        <span class="inline-block w-2 h-2 rounded-sm bg-purple-500" aria-hidden="true" />
        Memory
      </RouterLink>
      <RouterLink
        to="/output-skills"
        data-testid="sidebar-output-skills-link"
        class="flex items-center gap-2 px-2 py-1.5 rounded text-sm text-white/70 hover:text-white hover:bg-white/[0.04] no-underline transition-colors"
        active-class="!text-white bg-white/[0.06]"
      >
        <span class="inline-block w-2 h-2 rounded-sm bg-emerald-500" aria-hidden="true" />
        Output skills
      </RouterLink>
      <RouterLink
        to="/agent"
        data-testid="sidebar-agent-link"
        class="flex items-center gap-2 px-2 py-1.5 rounded text-sm text-white/70 hover:text-white hover:bg-white/[0.04] no-underline transition-colors"
        active-class="!text-white bg-white/[0.06]"
      >
        <span class="inline-block w-2 h-2 rounded-sm bg-sky-500" aria-hidden="true" />
        Agent
      </RouterLink>
    </nav>

    <!-- Scroll container; fade overlay sits on the parent so it doesn't scroll with the content. -->
    <div class="relative flex-1 min-h-0">
      <div class="h-full overflow-y-auto">
        <!-- Menu-shaped skeleton: a couple of category-header bars
             followed by leaf-row bars so the user sees roughly the
             eventual shape of the catalog instead of a centered spinner. -->
        <div v-if="!loaded" class="flex flex-col gap-2 p-3" data-testid="sidebar-skeleton">
          <NSkeleton text :repeat="1" class="!h-5 !w-1/2" />
          <NSkeleton text :repeat="3" class="!h-4 !ml-3" />
          <NSkeleton text :repeat="1" class="!h-5 !w-2/5 !mt-2" />
          <NSkeleton text :repeat="3" class="!h-4 !ml-3" />
        </div>

        <NAlert v-else-if="loadError" type="error" :show-icon="true" :title="loadError" class="m-3">
          <template #default>
            <NButton size="small" @click="emit('retry-load')">Retry</NButton>
          </template>
        </NAlert>

        <NEmpty
          v-else-if="pages.length === 0"
          description="No wiki pages yet — trigger an ingest to populate."
          class="p-8"
        />

        <!-- Search mode — the FTS results panel is its own component
             so the searching/error/empty/results branches stay tested
             in isolation and the parent can focus on tree wiring. -->
        <WikiSidebarSearchResults
          v-else-if="isSearchMode"
          :results="searchResults"
          :searching="searchPending"
          :error="searchError"
          :query="searchQuery"
          @select-path="onSearchSelect"
          @retry="runSearch(searchQuery.trim())"
          @clear="clearSearch"
        />

        <!-- Catalog mode — see `catalogTreeOptions` + `treePattern`
             computeds for the option-shape + pattern semantics. -->
        <NTree
          v-else
          data-testid="catalog-menu"
          :data="catalogTreeOptions"
          :selected-keys="selectedTreeKeys"
          v-model:expanded-keys="expandedKeys"
          :render-label="renderLabel"
          :override-default-node-click-behavior="rowClickBehavior"
          :pattern="treePattern"
          block-line
          virtual-scroll
          :show-irrelevant-nodes="false"
          class="h-full"
          @update:selected-keys="onTreeSelect"
        />
      </div>

      <!-- Bottom fade signalling more content scrolls offscreen. -->
      <div
        class="pointer-events-none absolute left-0 right-0 bottom-0 h-6 bg-gradient-to-t from-surface-bg to-transparent"
        aria-hidden="true"
      />
    </div>

    <!-- Right-edge drag handle (desktop only). See `useSidebarWidth`
         for the persistence + clamp logic. -->
    <div
      v-if="!isMobile"
      class="hidden sm:block absolute top-0 right-0 h-full w-1 cursor-col-resize hover:bg-brand-primary/30 transition-colors z-10"
      data-testid="sidebar-resize-handle"
      role="separator"
      aria-orientation="vertical"
      aria-label="Resize sidebar"
      @pointerdown="startResize"
    />
  </aside>
</template>
